<template>
  <ul ref="vertList" class="vert-list c-availability-list">
    <li v-for="(weeks, monthStart) in availabilityList" :key="monthStart" class="vert-list__item">
      <div class="heading-bar">
        <h1 class="hdg hdg--4">
          {{ monthHeader(monthStart) }}
        </h1>
      </div>

      <div v-for="(days, weekStart) in weeks" :key="weekStart">
        <div v-if="showWeekHeader(monthStart, weekStart, timeZone)" class="heading-bar-underline">
          <h2 class="section-heading">{{ weekHeader(weekStart) }}</h2>
        </div>
        <ul class="vert-list">
          <li v-for="(cards, date) in days" :key="date" class="vert-list__item">
            <div class="listing">
              <div class="listing__identifier">
                <div class="listing__top">{{ dayHeader(date) }}</div>
                <div class="listing__bottom">{{ dateHeader(date) }}</div>
              </div>
              <div class="listing__group">
                <AvailabilityCard
                  v-for="edge in cards"
                  :key="edge.node.start"
                  :availability="edge"
                  :active="isActive(edge)"
                  :time-zone="timeZone"
                  @selected="availabilitySelected"
                />
              </div>
            </div>
          </li>
        </ul>
      </div>
    </li>
  </ul>
</template>

<script lang="ts">
import { parseISO, addDays, format, isBefore, isAfter, parse } from 'date-fns';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import { toZonedTime } from 'date-fns-tz';
import AvailabilityCard from './AvailabilityCard.vue';
import {
  generateMonths,
  showWeekHeader,
  monthKey,
  weekKey,
  dayKey,
  monthHeader,
  weekHeader,
  DEFAULT_HOME_TIME_ZONE,
} from '../../lib/availability';

export default {
  name: 'AvailabilityList',
  components: {
    AvailabilityCard,
  },
  props: {
    availabilities: {
      type: Object,
      required: true,
    },
    selectedAvailability: {
      type: Object,
      default: null,
    },
    timeZone: {
      type: String,
      default: DEFAULT_HOME_TIME_ZONE,
    },
  },
  emits: ['scrollEnd', 'availabilitySelected'],
  data(): any {
    return {
      debouncedScroll: null,
    };
  },
  computed: {
    availabilityList(): any {
      if (this.availabilities && this.availabilities.edges) {
        // Scaffold and bucket in the same home-TZ wall clocks so week/month
        // boundaries never disagree with browser-local calendar math.
        let earliest = toZonedTime(new Date(), this.timeZone);
        let latest = addDays(earliest, 30);

        this.availabilities.edges.forEach((edge) => {
          const d = toZonedTime(parseISO(edge.node.start), this.timeZone);
          if (isBefore(d, earliest)) earliest = d;
          else if (isAfter(d, latest)) latest = d;
        });

        const months = generateMonths(earliest, latest);

        this.availabilities.edges.forEach((edge) => {
          const zoned = toZonedTime(parseISO(edge.node.start), this.timeZone);
          const mk = monthKey(zoned);
          const wk = weekKey(zoned);
          const dk = dayKey(zoned);

          if (!months[mk]) months[mk] = {};
          if (!months[mk][wk]) months[mk][wk] = {};
          if (!months[mk][wk][dk]) months[mk][wk][dk] = [];
          months[mk][wk][dk].push(edge);
        });

        return months;
      }
      return {};
    },
  },
  mounted() {
    this.debouncedScroll = debounce(this.handleScroll, 100);
    window.addEventListener('scroll', this.debouncedScroll);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.debouncedScroll);
  },
  methods: {
    showWeekHeader,
    monthHeader,
    weekHeader,
    // date keys are home-TZ calendar days (yyyy-MM-dd); parse as plain dates, not UTC midnights
    dayHeader(day: string): string {
      return format(parse(day, 'yyyy-MM-dd', new Date()), 'eee');
    },
    dateHeader(day: string): string {
      return format(parse(day, 'yyyy-MM-dd', new Date()), 'd');
    },
    handleScroll() {
      if (this.$refs.vertList?.getBoundingClientRect().bottom < window.innerHeight) this.$emit('scrollEnd');
    },
    isEmpty(ob): boolean {
      return isEmpty(ob);
    },
    availabilitySelected(availability) {
      this.$emit('availabilitySelected', availability);
    },
    isActive(edge: any): boolean {
      // Cursors are array offsets that repeat once paged windows are merged; start times don't.
      return this.selectedAvailability && this.selectedAvailability.node?.start === edge.node?.start;
    },
  },
};
</script>
